import React, { useEffect } from "react";

import { isEmpty } from "lodash/fp";
import { useParams, useSearchParams } from "react-router-dom";
import { shallow } from "zustand/shallow";

import ReportLoading from "@app/analytics/components/ReportLoading";
import {
  ContentColumnContainer,
  InnerContainer,
  SectionDivider,
} from "@app/analytics/components/reports/styles";
import useReportAcceptInvite from "@app/analytics/hooks/useReportAcceptInvite";
import ReportRequestAccess from "@app/analytics/ReportRequestAccess";
import LimitedDataBanner from "@app/analytics/TopicReport/components/LimitedDataBanner";
import {
  ActivelySharingBanner,
  SharedReportBanner,
} from "@app/analytics/TopicReport/components/SharingBanner";
import useTopicReport from "@app/analytics/TopicReport/hooks/useTopicReport";
import useTopicReportEntities from "@app/analytics/TopicReport/hooks/useTopicReportEntities";
import useTopicReportImpersonations from "@app/analytics/TopicReport/hooks/useTopicReportImpersonations";
import useTopicReportStore from "@app/analytics/TopicReport/hooks/useTopicReportStore";
import {
  TopicReportContextProvider,
  useTopicReportContext,
} from "@app/analytics/TopicReport/TopicReportContext";
import TopicReportError from "@app/analytics/TopicReport/TopicReportError";
import useAuth from "@app/common/auth/useAuth";
import useMeasure from "@app/common/hooks/useMeasure";
import { toast } from "@app/common/hooks/useToasts";
import useWindowDimensions from "@app/common/hooks/useWindowDimensions";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import Stack from "@rui/Stack";

import FooterNavigation from "./components/FooterNavigation";
import SharedTopicReportDetails from "./components/SharedTopicReportDetails";
import * as Events from "./events";
import {
  ContentColumnWithFooter,
  MainContentContainer,
  OuterContainer,
} from "./styles";
import TopicReportFeedback from "./TopicReportFeedback";
import TopicReportHeader from "./TopicReportHeader";
import TopicReportNotes from "./TopicReportNotes";
import TopicReportSidebar from "./TopicReportSidebar";
import TopicReportSidebarContent from "./TopicReportSidebar/TopicReportSidebarContent";
import TopicReportTimeline from "./TopicReportTimeline";
import type { TopicReportTab } from "./types";

const MOBILE_BREAKPOINT = 900; // == the "md" breakpoint
const SHARED_MAIN_CONTENT_CONTAINER_SX = {
  maxWidth: "800px",
  margin: "0 auto",
};

const TopicReport = () => {
  usePageTitle("Readout");
  const { user } = useAuth();

  const [queryParams] = useSearchParams();
  const { topicId } = useParams();
  const reportInvite = queryParams.get("mInvite");
  const impersonateId = queryParams.get("iid");

  const { windowWidth } = useWindowDimensions();
  const { mainScrollContainer, setMainScrollContainer } =
    useTopicReportContext();
  const [headerContainerRef, , { innerHeight }] = useMeasure();

  const {
    isError: isTopicError,
    isLoading: isTopicLoading,
    isSharedReport,
    topic,
    responseError,
  } = useTopicReport();

  const { isError: isTopicEntitiesError, isLoading: isTopicEntitiesLoading } =
    useTopicReportEntities();

  const {
    isLoading: isImpersonationsLoading,
    isError: isImpersonationsError,
    impersonations,
  } = useTopicReportImpersonations();

  const isError = isTopicError || isTopicEntitiesError || isImpersonationsError;

  const { isLoading: isInviting } = useReportAcceptInvite(reportInvite, {
    enabled: isError && Boolean(reportInvite),
    onError: () => {
      toast.error("Oh no! Your report invite has expired.");
    },
  });

  const { selectedTab, setSelectedTab, setShareDialogOpen } =
    useTopicReportStore(
      (state) => ({
        selectedTab: state.selectedTab,
        setSelectedTab: state.setSelectedTab,
        setShareDialogOpen: state.setShareDialogOpen,
      }),
      shallow
    );

  useEffect(() => {
    if (topic?.id) {
      Events.topicReportView({ channelProvider: topic.provider });
    }
  }, [topic?.id]);

  useEffect(() => {
    if (queryParams.get("action") === "ignoreShare") {
      toast.info("Share request ignored");
    }
  }, [queryParams.get("action")]);

  const isLoading =
    isTopicLoading ||
    isTopicEntitiesLoading ||
    isImpersonationsLoading ||
    isInviting;

  if (isLoading) {
    return <ReportLoading />;
  }

  if (
    isError &&
    user &&
    !isEmpty(topicId) &&
    responseError?.error === "unauthorized" &&
    !isEmpty(impersonateId)
  ) {
    return (
      <ReportRequestAccess
        reportId={topicId}
        reportType="topic"
        ownerId={impersonateId}
        requesterId={user.id}
      />
    );
  }

  if (isError) {
    return <TopicReportError kind={responseError?.error ?? "unknown"} />;
  }

  const handleFooterNavTabChange = (tab: TopicReportTab) => {
    if (mainScrollContainer) {
      mainScrollContainer.scrollTop = 0;
    }
    setSelectedTab(tab);
  };

  const onNotesTab =
    isSharedReport ||
    windowWidth >= MOBILE_BREAKPOINT ||
    selectedTab === "notes";

  return (
    <OuterContainer heightOffset={innerHeight}>
      <Stack ref={headerContainerRef}>
        <TopicReportHeader />
        {topic && <LimitedDataBanner topic={topic} />}
        {isSharedReport ? (
          <SharedReportBanner owner={topic?.ownerName} />
        ) : !isEmpty(impersonations) ? (
          <ActivelySharingBanner
            numPeopleWithAccess={impersonations.length}
            onClickShare={() => setShareDialogOpen(true)}
          />
        ) : null}
      </Stack>
      <InnerContainer className="topic-report__inner-container">
        <ContentColumnContainer>
          <ContentColumnWithFooter
            heightOffset={isSharedReport ? 0 : 64} // 64px = height of the footer
            ref={setMainScrollContainer}
            sx={onNotesTab ? { bgcolor: "background.default" } : undefined}
          >
            {onNotesTab ? (
              <MainContentContainer
                spacing={2}
                sx={
                  isSharedReport ? SHARED_MAIN_CONTENT_CONTAINER_SX : undefined
                }
              >
                {isSharedReport && <SharedTopicReportDetails topic={topic} />}
                <TopicReportNotes />
                {!isSharedReport && (
                  <>
                    <SectionDivider />
                    <TopicReportTimeline />
                  </>
                )}
              </MainContentContainer>
            ) : selectedTab === "sources" ? (
              <TopicReportSidebarContent tab="sources" />
            ) : (
              <TopicReportSidebarContent tab="overview" />
            )}
          </ContentColumnWithFooter>
          {!isSharedReport && (
            <FooterNavigation
              onChange={handleFooterNavTabChange}
              selected={selectedTab}
            />
          )}
        </ContentColumnContainer>
        {!isSharedReport && <TopicReportSidebar />}
      </InnerContainer>
      <TopicReportFeedback topic={topic} />
    </OuterContainer>
  );
};

const TopicReportWrapper = () => (
  <TopicReportContextProvider>
    <TopicReport />
  </TopicReportContextProvider>
);

export default TopicReportWrapper;
